import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  MdAdd,
  MdFilterListOff,
  MdGrass,
  MdRefresh,
} from 'react-icons/md';
import { Page } from '@components/layout/Page';
import { PageEmpty } from '@components/layout/PageEmpty';
import { DataPagination } from '@components/ui/DataPagination';
import { GridSkeleton } from '@components/ui/GridSkeleton';
import { TabFilter } from '@components/ui/TabFilter';
import { useCropOptions } from '@features/crops/hooks';
import type { CropOption } from '@features/crops/schema';
import { VarietyFormDialog } from '@features/varieties/components/VarietyFormDialog';
import { VarietiesGrid } from '@features/varieties/components/VarietiesGrid';
import { useVarieties } from '@features/varieties/hooks';
import {
  type ListVarietiesParams,
  type SortDirection,
  type Variety,
  type VarietySortKey,
} from '@features/varieties/schema';

const DEFAULT_PAGE_SIZE = 50;

type ActiveTabValue = '' | 'true' | 'false';

const ACTIVE_TABS: ReadonlyArray<{ value: ActiveTabValue; label: string }> = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

export default function VarietiesScreen() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState<ActiveTabValue>('');
  const [cropId, setCropId] = useState<number | null>(null);
  const [sort, setSort] = useState<{
    sortBy?: VarietySortKey;
    sortDir?: SortDirection;
  }>({});

  useEffect(() => {
    const t = setTimeout(() => setSearchText(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchText, activeTab, cropId, pageSize, sort]);

  const params = useMemo<ListVarietiesParams>(
    () => ({
      page,
      pageSize,
      searchText: searchText || undefined,
      cropId: cropId ?? undefined,
      isActive: activeTab || undefined,
      sortBy: sort.sortBy,
      sortDir: sort.sortDir,
    }),
    [page, pageSize, searchText, cropId, activeTab, sort],
  );

  const { data, isLoading, isFetching, refetch } = useVarieties(params);
  const { data: crops = [] } = useCropOptions();

  const cropById = useMemo(
    () => new Map(crops.map((c) => [c.id, c])),
    [crops],
  );
  const cropValue: CropOption | null =
    cropId == null ? null : cropById.get(String(cropId)) ?? null;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Variety | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (row: Variety) => {
    setEditing(row);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const totalRecords = data?.paginationDetails?.totalRecords ?? 0;
  const hasActiveFilters = Boolean(searchText || activeTab || cropId !== null);
  const isInitialLoad = isLoading && !data;
  const showEmpty = !isLoading && (data?.rowData?.length ?? 0) === 0;

  const clearFilters = () => {
    setSearchInput('');
    setSearchText('');
    setActiveTab('');
    setCropId(null);
  };

  return (
    <Page
      title="Varieties"
      subtitle="Crop sub-types tracked at each mandi (long staple, basmati, etc.)."
      fullHeight
      headerRightSlot={
        <TabFilter
          options={ACTIVE_TABS}
          current={activeTab}
          onChange={setActiveTab}
          ariaLabel="Filter by active state"
        />
      }
      topBarAction={
        <Button
          variant="contained"
          size="small"
          startIcon={<MdAdd />}
          onClick={openCreate}
        >
          New
        </Button>
      }
    >
      <div className="mb-3 flex flex-col gap-3 rounded-xl bg-surface-muted px-3 py-3 md:flex-row md:flex-wrap md:items-end md:px-4">
        <TextField
          label="Search code or name"
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full md:min-w-[220px] md:flex-1"
        />
        <Autocomplete
          size="small"
          options={crops}
          value={cropValue}
          onChange={(_, opt) => setCropId(opt ? Number(opt.id) : null)}
          getOptionLabel={(opt) => opt.name}
          isOptionEqualToValue={(opt, v) => opt.id === v.id}
          className="w-full md:w-[200px]"
          renderInput={(p) => <TextField {...p} label="Crop" />}
        />
        <div className="flex items-end gap-2 md:contents">
          <TextField
            label="Page size"
            size="small"
            select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="flex-1 md:w-[110px] md:flex-initial"
          >
            {[25, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>

          <div className="flex items-center gap-2 md:ml-auto">
            {hasActiveFilters && (
              <Button variant="text" size="small" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
            <Tooltip title={isFetching ? 'Loading…' : 'Refresh'}>
              <span>
                <IconButton onClick={() => refetch()} size="small" disabled={isFetching}>
                  {isFetching ? <CircularProgress size={16} thickness={5} /> : <MdRefresh />}
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-surface">
        {isFetching && data && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 5,
              height: 2,
            }}
          />
        )}
        {isInitialLoad ? (
          <GridSkeleton
            rows={6}
            columns={[
              { width: '70px', bar: 'w-9' },
              { width: '1fr', bar: 'w-1/2' },
              { width: '1fr', bar: 'w-1/2' },
              { width: '2fr', bar: 'w-3/4' },
              { width: '110px', bar: 'w-14' },
              { width: '200px', bar: 'w-24' },
              { width: '100px', empty: true },
            ]}
          />
        ) : showEmpty ? (
          hasActiveFilters ? (
            <PageEmpty
              icon={MdFilterListOff}
              title="No matches"
              message="Nothing matches the current filters. Try widening your search."
              action={
                <Button variant="outlined" size="small" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <PageEmpty
              icon={MdGrass}
              title="No varieties yet"
              message="Add a variety to a crop to start configuring price series."
              action={
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<MdAdd />}
                  onClick={openCreate}
                >
                  New variety
                </Button>
              }
            />
          )
        ) : (
          <VarietiesGrid data={data} onEdit={openEdit} onSortChange={setSort} />
        )}
      </div>

      <DataPagination
        page={page}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={setPage}
      />

      <VarietyFormDialog open={dialogOpen} variety={editing} onClose={closeDialog} />
    </Page>
  );
}
